// ./src/components/features/Budget/BudgetPaymentsDrawer/BudgetPaymentsDrawer.tsx
//
// Manage the payments made toward one budget item. Opened from the item's row.
// Shows a paid / remaining summary, the list of payments (amount, method, date,
// who paid) with edit / delete, and an "Add payment" button that opens the
// BudgetPaymentDrawer stacked on top. Changes save immediately and call onChanged
// so the item's derived paid / remaining refresh in the list + summary cards.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Stack, Group, Text, Button, Card, Box, ActionIcon, Tooltip, SimpleGrid, Badge, Divider } from "@mantine/core";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import {
  BudgetItem,
  BudgetPayment,
  BudgetPaymentService,
  EventGuest,
  EventGuestService,
  GuestContribution,
  GuestContributionService,
  contributionKind,
  isContributionConfirmed,
  getGuestDisplayName,
  listAllPages,
} from "@/utils/api";
import { useBudgetPayments, useToast } from "@/hooks";
import { formatCurrency, refusalReason } from "@/utils";
import { AppDrawer, AppIcon, EmptyState, ErrorAlert, ConfirmModal, SwipeableCard, SwipeAction, useSwipeableList, CardListSkeleton } from "@/components/generic";

import { BudgetPaymentDrawer } from "../BudgetPaymentDrawer/BudgetPaymentDrawer";
import { getPaymentMethodIcon, getPaymentMethodLabel, getPartyKindIcon, formatDueDate } from "../budgetHelpers";

export interface BudgetPaymentsDrawerProps {
  opened: boolean;
  onClose: () => void;
  item: BudgetItem | null;
  eventId: string;
  /** Refresh the item's derived paid / remaining after any change. */
  onChanged: () => void;
}

export const BudgetPaymentsDrawer: React.FC<BudgetPaymentsDrawerProps> = ({ opened, onClose, item, eventId, onChanged }) => {
  const toast = useToast();
  const budgetItemId = item?.id ?? null;
  const currency = item?.currencyCode || "USD";

  const { payments, isLoaded, error: paymentsError, reload } = useBudgetPayments(opened ? budgetItemId : null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<BudgetPayment | null>(null);
  const [deletePayment, setDeletePayment] = useState<BudgetPayment | null>(null);

  // Guest pledges / self-reported payments toward THIS item (so the host sees the full picture:
  // paid + pledged + remaining). Confirmed ones already appear as real payments above, so the
  // section below shows only the outstanding (unconfirmed) ones.
  const [contributions, setContributions] = useState<GuestContribution[]>([]);
  const [guests, setGuests] = useState<EventGuest[]>([]);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const loadContributions = useCallback(async () => {
    if (!budgetItemId) {
      return;
    }

    const [contribRes, guestsRes] = await Promise.all([
      listAllPages(GuestContributionService.list, { eventId: [eventId], budgetItemId: [budgetItemId] }),
      listAllPages(EventGuestService.list, { eventId: [eventId] }),
    ]);

    if (contribRes.code === OP_RESULT_CODES.OK && Array.isArray(contribRes.data)) {
      setContributions(contribRes.data as GuestContribution[]);
    }
    if (guestsRes.code === OP_RESULT_CODES.OK && Array.isArray(guestsRes.data)) {
      setGuests(guestsRes.data as EventGuest[]);
    }
  }, [budgetItemId, eventId]);

  useEffect(() => {
    if (opened && budgetItemId) {
      setContributions([]);
      loadContributions();
    }
  }, [opened, budgetItemId, loadContributions]);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const paidTotal = useMemo(() => payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0), [payments]);
  const expected = Number(item?.expectedAmount) || 0;
  const remaining = expected - paidTotal;

  const guestNameById = useMemo(() => {
    const map: Record<string, string> = {};
    guests.forEach((g) => {
      if (g.id) {
        map[g.id] = getGuestDisplayName(g);
      }
    });

    return map;
  }, [guests]);

  // Outstanding (unconfirmed) guest contributions toward this item; confirmed ones became real
  // payments and are counted in paidTotal instead.
  const outstanding = useMemo(() => contributions.filter((c) => !isContributionConfirmed(c)), [contributions]);
  const pledgedTotal = useMemo(
    () => outstanding.reduce((sum, c) => sum + (Number(c.amount) || 0), 0),
    [outstanding]
  );
  const hasPledges = outstanding.length > 0;

  const afterChange = useCallback(async () => {
    await Promise.all([reload(), loadContributions()]);
    onChanged();
  }, [reload, loadContributions, onChanged]);

  const confirmContribution = useCallback(
    async (c: GuestContribution) => {
      if (!c.id) {
        return;
      }

      setConfirmingId(c.id);
      const result = await BudgetPaymentService.confirmContribution({ contributionId: c.id });
      setConfirmingId(null);

      // No success toast — the pledge leaving the list and Paid going up IS the confirmation.
      if (result.didSucceed()) {
        afterChange();
      } else {
        toast.error(refusalReason(result) || "Could not confirm the payment.");
      }
    },
    [afterChange, toast]
  );

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (p: BudgetPayment) => {
    setEditing(p);
    setFormOpen(true);
  };

  const confirmDelete = useCallback(async () => {
    const target = deletePayment;
    setDeletePayment(null);

    if (!target?.id) {
      return;
    }

    const result = await BudgetPaymentService.remove(target.id);

    // No success toast — the row disappearing IS the confirmation (house rule).
    if (result.didSucceed()) {
      afterChange();
    } else {
      toast.error(refusalReason(result) || "Could not remove the payment.");
    }
  }, [deletePayment, afterChange, toast]);

  const paymentTitle = (p: BudgetPayment): string => formatCurrency(p.amount ?? 0, currency);

  const paymentSubline = (p: BudgetPayment): string =>
    [getPaymentMethodLabel(p.paymentMethod), formatDueDate(p.paidAt), p.paidByLabel]
      .filter(Boolean)
      .join(" · ");

  const rowActions = (p: BudgetPayment) => (
    <Group gap={4} wrap="nowrap">
      <Tooltip label="Edit" withArrow position="bottom">
        <ActionIcon variant="subtle" color="blue" onClick={() => openEdit(p)} aria-label="Edit">
          <AppIcon iconName="Edit" size={16} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Delete" withArrow position="bottom">
        <ActionIcon variant="subtle" color="red" onClick={() => setDeletePayment(p)} aria-label="Delete">
          <AppIcon iconName="Trash" size={16} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );

  const swipeActionsFor = (p: BudgetPayment): SwipeAction[] => [
    { key: "edit", icon: "Edit", label: "Edit", color: "blue", onClick: () => { closeAllCards(); openEdit(p); } },
    { key: "delete", icon: "Trash", label: "Delete", color: "red", onClick: () => { closeAllCards(); setDeletePayment(p); } },
  ];

  const paymentRow = (p: BudgetPayment) => (
    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
      <AppIcon iconName={p.paidByKind ? getPartyKindIcon(p.paidByKind) : getPaymentMethodIcon(p.paymentMethod)} size={18} />
      <Box style={{ minWidth: 0 }}>
        <Text fw={600} size="md" truncate>
          {paymentTitle(p)}
        </Text>
        <Text size="sm" c="dimmed" truncate>
          {paymentSubline(p) || "—"}
        </Text>
        {p.note ? (
          <Text size="sm" c="dimmed" truncate>
            {p.note}
          </Text>
        ) : null}
      </Box>
    </Group>
  );

  const cardBody = (p: BudgetPayment, showActions: boolean) => (
    <Card withBorder radius="md" padding="md">
      <Group justify="space-between" wrap="nowrap" gap="sm">
        {paymentRow(p)}
        {showActions && rowActions(p)}
      </Group>
    </Card>
  );

  const body = () => {
    if (!isLoaded) {
      return (
        <CardListSkeleton rows={3} actions={2} leading={18} leadingShape="square" lines={2} lineSizes={['md', 'sm']} lineGap={0} />
      );
    }

    // ⚠ A failed read is never drawn as "Paid 0 · Remaining = everything" with "No payments yet" under it — the totals
    // are computed from the rows that did not arrive (em-scaffold-ui: a failed read is the third empty-looking state).
    if (paymentsError) {
      return (
        <Stack gap="sm">
          <ErrorAlert error={paymentsError} title="Couldn’t load the payments" page="Budget" operation="eventBudgetPaymentList" />
          {/* A drawer has no header Refresh — the way back from a failed read is here. */}
          <Button variant="light" leftSection={<AppIcon iconName="Refresh" size={16} />} onClick={() => void reload()} style={{ alignSelf: "flex-start" }}>
            Try again
          </Button>
        </Stack>
      );
    }

    return (
      <Stack gap="sm">
        <SimpleGrid cols={hasPledges ? 2 : 3} spacing="xs">
          <Card withBorder radius="md" padding="xs">
            <Text size="xs" c="dimmed">
              Expected
            </Text>
            <Text fw={700}>{formatCurrency(expected, currency)}</Text>
          </Card>
          <Card withBorder radius="md" padding="xs">
            <Text size="xs" c="dimmed">
              Paid
            </Text>
            <Text fw={700} c="teal">
              {formatCurrency(paidTotal, currency)}
            </Text>
          </Card>
          {hasPledges ? (
            <Card withBorder radius="md" padding="xs">
              <Text size="xs" c="dimmed">
                Pledged
              </Text>
              <Text fw={700} c="blue">
                {formatCurrency(pledgedTotal, currency)}
              </Text>
            </Card>
          ) : null}
          <Card withBorder radius="md" padding="xs">
            <Text size="xs" c="dimmed">
              Remaining
            </Text>
            <Text fw={700} c={remaining > 0 ? "red" : undefined}>
              {formatCurrency(remaining, currency)}
            </Text>
            {hasPledges && remaining > 0 ? (
              <Text size="xs" c="dimmed">
                {formatCurrency(Math.max(remaining - pledgedTotal, 0), currency)} after pledges
              </Text>
            ) : null}
          </Card>
        </SimpleGrid>

        {payments.length === 0 ? (
          <EmptyState
            iconName="Cash"
            title="No payments yet"
            description="Record what you've paid toward this item to track how much is left."
            actionLabel="Add payment"
            actionIcon="Plus"
            onAction={openAdd}
          />
        ) : (
          <>
            <Stack gap="xs">
              {payments
                .filter((p) => p.id)
                .map((p) => (
                  <React.Fragment key={p.id}>
                    <Box hiddenFrom="sm">
                      <SwipeableCard
                        leftSwipeActions={swipeActionsFor(p)}
                        forceClose={shouldForceClose(p.id || "")}
                        onForceCloseComplete={() => handleForceCloseComplete(p.id || "")}
                        onSwipeStateChange={(isOpen) => isOpen && openCard(p.id || "")}
                        borderRadius="var(--mantine-radius-md)"
                      >
                        {cardBody(p, false)}
                      </SwipeableCard>
                    </Box>
                    <Box visibleFrom="sm">{cardBody(p, true)}</Box>
                  </React.Fragment>
                ))}
            </Stack>

            <Button variant="light" leftSection={<AppIcon iconName="Plus" size={16} />} onClick={openAdd} mt="xs">
              Add payment
            </Button>
          </>
        )}

        {hasPledges ? (
          <>
            <Divider label="Guest pledges & payments" labelPosition="left" mt="xs" />
            <Stack gap="xs">
              {outstanding.map((c) => {
                const kind = contributionKind(c);
                const guestName = (c.eventGuestId && guestNameById[c.eventGuestId]) || "Guest";

                return (
                  <Card key={c.id} withBorder radius="md" padding="md">
                    <Group justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
                      <Box style={{ minWidth: 0, flex: 1 }}>
                        <Group gap="xs" wrap="nowrap">
                          <Text fw={700} size="md">
                            {formatCurrency(c.amount ?? 0, currency)}
                          </Text>
                          {kind === "paid" ? (
                            <Badge color="orange" variant="light" size="sm">
                              Says paid
                            </Badge>
                          ) : (
                            <Badge color="blue" variant="light" size="sm">
                              Pledge
                            </Badge>
                          )}
                        </Group>
                        <Text size="sm" c="dimmed" truncate>
                          {guestName}
                        </Text>
                        {c.note ? (
                          <Text size="sm" c="dimmed" lineClamp={2}>
                            {c.note}
                          </Text>
                        ) : null}
                      </Box>

                      <Button
                        size="xs"
                        variant="light"
                        color="teal"
                        leftSection={<AppIcon iconName="Check" size={14} />}
                        loading={confirmingId === c.id}
                        onClick={() => confirmContribution(c)}
                      >
                        {kind === "paid" ? "Confirm" : "Record paid"}
                      </Button>
                    </Group>
                  </Card>
                );
              })}
            </Stack>
            <Text size="xs" c="dimmed">
              Pledges and “says paid” amounts don’t count toward Paid until you record them. Recording either one posts a real payment on this item.
            </Text>
          </>
        ) : null}
      </Stack>
    );
  };

  return (
    <>
      <AppDrawer
        opened={opened}
        onClose={onClose}
        title={item ? `Payments — ${item.name ?? ""}` : "Payments"}
        size="md"
        actionType="close"
        cancelLabel="Close"
      >
        {body()}
      </AppDrawer>

      {budgetItemId && (
        <BudgetPaymentDrawer
          opened={formOpen}
          onClose={() => setFormOpen(false)}
          budgetItemId={budgetItemId}
          eventId={eventId}
          currencyCode={currency}
          payment={editing}
          onSaved={afterChange}
        />
      )}

      <ConfirmModal
        opened={!!deletePayment}
        onClose={() => setDeletePayment(null)}
        onConfirm={confirmDelete}
        type="danger"
        title="Remove this payment?"
        message="The item's paid and remaining amounts will update."
        confirmLabel="Remove"
        cancelLabel="Cancel"
      />
    </>
  );
};

export default BudgetPaymentsDrawer;
